import { useEffect, useMemo } from 'react';
import {
	PropertyContainer,
	PropertyContainerRow,
} from '~/components/ui/PropertyContainer';
import { Slider } from '~/components/ui/Slider';
import { Text } from '~/components/ui/Text';
import { rage, useObservable } from '~/rage';
import { AppearanceFaceMorphConfig, PedFaceMorphSection } from '~/shared';
import {
	setFaceMorphValue,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { Section } from '../Section';
import { SectionHeading } from '../SectionHeading';
import { faceMorphIcons, faceMorphNames } from './AppearanceTab.model';
import {
	StyledContainer,
	StyledSettingsContainer,
} from './AppearanceTab.styled';

const AppearanceTab: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const faceMorphConfigs = useObservable(
		rage.characterCreation.getFaceMorphConfigs(),
	);
	const faceMorphValues = useLayoutSelector(
		(state) => state.characterCreation.faceMorphValues,
	);

	const faceMorphConfigsByGroups = useMemo(() => {
		return faceMorphConfigs.reduce<Record<string, AppearanceFaceMorphConfig[]>>(
			(groups, config) => {
				const { section } = config;
				if (!groups[section]) {
					groups[section] = [];
				}
				groups[section].push(config);
				return groups;
			},
			{},
		);
	}, [faceMorphConfigs]);

	const handleChange = (index: number, value: number) => {
		dispatch(setFaceMorphValue({ index, value }));
	};

	useEffect(() => {
		rage.characterCreation
			.setFaceMorphValues(faceMorphValues)
			.catch((err) => rage.notifications.showException(err));
	}, [faceMorphValues]);

	return (
		<StyledContainer>
			<StyledSettingsContainer tabIndex={-1}>
				{Object.keys(faceMorphConfigsByGroups).map((item, i) => {
					const items = faceMorphConfigsByGroups[
						item
					] as AppearanceFaceMorphConfig[];
					return (
						<Section key={i}>
							<SectionHeading
								icon={faceMorphIcons[item as PedFaceMorphSection]}
								text={faceMorphNames[item as PedFaceMorphSection]}
							/>
							<PropertyContainer>
								{items.map((config) => {
									const value = faceMorphValues.find(
										(value) => value.index === config.index,
									);

									return (
										<PropertyContainerRow key={config.index}>
											<Text>{config.name}</Text>
											<Slider
												value={value?.value || 0}
												min={-1}
												max={1}
												onChange={(newValue) =>
													handleChange(config.index, newValue)
												}
												valueInput
												center
											/>
										</PropertyContainerRow>
									);
								})}
							</PropertyContainer>
						</Section>
					);
				})}
			</StyledSettingsContainer>
		</StyledContainer>
	);
};

export { AppearanceTab };
